import { useMemo, useState } from 'react';

import useOpenCustomScheduleExpectations from '@/hooks/useOpenCustomScheduleExpectations';
import usePrograms from '@/hooks/usePrograms';
import type { Program } from '@/types/program';
import { Alert, Button, Checkbox, Modal, Spinner, Typography } from '@/ui';
import groupEndingCustomExpectations from '@/utils/groupEndingCustomExpectations';
import isDualDiagnosisProgram from '@/utils/isDualDiagnosisProgram';
import snakeToTitleCase from '@/utils/snakeToTitleCase';

import EndingCustomExpectations from './components/EndingCustomExpectations';
import ProgramChangeSummary from './components/ProgramChangeSummary';
import './styles.css';
import type { Props } from './types';

const MENTAL_HEALTH = 'mental_health';
const OPIOIDS = 'opioids';

/**
 * Everything the change does, phrased to finish "I intend to …" so staff
 * confirm exactly what they are doing. Every enrollment and transition is
 * acknowledged the same way, so the workflow is the same to learn; the
 * summary still highlights the changes that go against clinical guidance.
 */
const intendedChanges = (
  kind: Props['kind'],
  fromProgram: Program | null,
  toProgram: Program
): string[] => {
  const toDual = isDualDiagnosisProgram(toProgram.name);

  if (kind === 'enrollment' || !fromProgram) {
    return [
      toDual
        ? `enroll this member directly into a Dual program (${toProgram.name})`
        : `enroll this member in ${toProgram.name}`
    ];
  }

  const changes: string[] = [];
  if (fromProgram.phase_type_name !== toProgram.phase_type_name) {
    changes.push(
      `change this member's level of care from ${snakeToTitleCase(fromProgram.phase_type_name)} to ${snakeToTitleCase(toProgram.phase_type_name)}`
    );
  }
  const fromDual = isDualDiagnosisProgram(fromProgram.name);
  if (!fromDual && toDual) changes.push('move this member into a Dual program');
  if (fromDual && !toDual) changes.push('take this member out of their Dual program');
  if (fromProgram.drug_category !== toProgram.drug_category) {
    changes.push(
      `change this member's primary concern from ${snakeToTitleCase(fromProgram.drug_category)} to ${snakeToTitleCase(toProgram.drug_category)}`
    );
  }
  return changes.length ? changes : [`move this member to ${toProgram.name}`];
};

/** What else submitting does, mirroring Phase.handle_phase_transition on the backend. */
const transitionSideEffects = (toProgram: Program): string[] => {
  const effects: string[] = [];
  if (isDualDiagnosisProgram(toProgram.name) && toProgram.drug_category !== OPIOIDS) {
    effects.push('creates the dual diagnosis medical starter sequence');
  }
  if (toProgram.drug_category === MENTAL_HEALTH) {
    effects.push("cashes out the member's pending rewards");
  }
  return effects;
};

const ProgramChangeReview = ({
  kind,
  fromProgram,
  fromPhaseId = null,
  toProgramId,
  isSubmitting = false,
  onBack,
  onConfirm
}: Props) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const programsQuery = usePrograms({ all_phase_types: true });
  const customExpectationsQuery = useOpenCustomScheduleExpectations(
    kind === 'transition' ? fromPhaseId : null
  );
  const programsResponse = programsQuery.data;
  const openCustomExpectations = customExpectationsQuery.data;

  const toProgram = programsResponse?.data?.find((program) => program.id === toProgramId);
  const endingItems = useMemo(
    () => groupEndingCustomExpectations(openCustomExpectations ?? []),
    [openCustomExpectations]
  );

  // Before the review loads, fall back to a generic intent so the checkbox
  // always sits above the actions; it stays disabled until there's a review.
  const changesToConfirm = toProgram
    ? intendedChanges(kind, fromProgram, toProgram)
    : [kind === 'enrollment' ? 'enroll this member' : 'transition this member'];
  const sideEffects = toProgram && kind === 'transition' ? transitionSideEffects(toProgram) : [];
  const isLoading = programsQuery.isPending || customExpectationsQuery.isLoading;
  // The program repository resolves failures as { isError } rather than throwing,
  // so check the payload as well as the query state. A failed load must not let
  // staff confirm an unreviewed change.
  const loadFailed =
    !isLoading &&
    (programsQuery.isError ||
      !!programsResponse?.isError ||
      customExpectationsQuery.isError ||
      !toProgram);
  const retry = () => {
    programsQuery.refetch();
    if (customExpectationsQuery.isError) customExpectationsQuery.refetch();
  };

  return (
    <Modal
      open={true}
      onClose={onBack}
      size="lg"
      title={kind === 'enrollment' ? 'Review enrollment' : 'Review phase change'}
      dataTestId="program-change-review"
      primaryAction={{
        label: kind === 'enrollment' ? 'Confirm enrollment' : 'Confirm transition',
        onClick: onConfirm,
        isLoading: isSubmitting,
        disabled: isLoading || loadFailed || !toProgram || isSubmitting || !acknowledged
      }}
      secondaryAction={{ label: 'Back', onClick: onBack, color: 'neutral', variant: 'outline' }}>
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : loadFailed || !toProgram ? (
          <Alert variant="error" dismissable={false}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>Couldn&apos;t load the details needed to review this change.</span>
              <Button color="neutral" variant="outline" size="sm" onClick={retry}>
                Retry
              </Button>
            </div>
          </Alert>
        ) : (
          <div className="flex flex-col gap-4">
            <ProgramChangeSummary fromProgram={fromProgram} toProgram={toProgram} />

            <EndingCustomExpectations items={endingItems} />

            {sideEffects.length > 0 && (
              <Typography muted>This transition also {sideEffects.join(' and ')}.</Typography>
            )}
          </div>
        )}

        <Checkbox
          id="program-change-acknowledgement"
          checked={acknowledged}
          onChange={setAcknowledged}
          disabled={isLoading || loadFailed || !toProgram}
          label={`I intend to ${changesToConfirm.join(' and ')}.`}
        />
      </div>
    </Modal>
  );
};

export default ProgramChangeReview;
